import { localize } from 'vee-validate'
import StorageService from '@/services/storage-service'
import i18n from '@/libs/i18n'
import store from '@/store'

export default class LocalizationService {
    private static readonly storageKey = 'okmenu_locale';

    private static loadedLanguages = ['en', 'ar']

    static initLocale() {
      const storedLocale = StorageService.getItem(LocalizationService.storageKey)
      const locale = `${(storedLocale || window?.navigator?.language || 'en')}`.substring(0, 2)
      this.updateLocale(locale)
    }

    static updateLocale(locale: string) {
      i18n.locale = locale
      LocalizationService.updateDirection(locale)
      setTimeout(() => store.dispatch('menu/REFRESH_MENU_LOCALIZATION'), 300)
      let validationLocale = 'en' // handle Chinese
      switch (locale) {
        case 'zh': validationLocale = 'zh_CN'; break
        case 'ur': validationLocale = 'en'; break
        default: validationLocale = 'en'
      }
       import(/* webpackChunkName: "lang-[request]" */ `@/libs/i18n/locales/${locale}.json`).then(
         messages => {
           i18n.setLocaleMessage(locale, messages.default)
           LocalizationService.loadedLanguages.push(locale)
         },
       )
      import(/* webpackChunkName: "validate-lang-[request]" */ `vee-validate/dist/locale/${validationLocale}.json`)
        .then(({ messages, code }) => {
          // @ts-ignore
          localize(locale, { code, messages: { ...messages, ...(i18n.t('validations', locale) ?? {}) } })
        })
        .catch(error => { console.error('Error while fetching validation messages', error) })
    }

    static translate(key: string) {
      return i18n.t(key)
    }

    static setLocale(locale: string) {
      LocalizationService.updateLocale(locale)
      StorageService.setItem(LocalizationService.storageKey, locale)
    }

    static setLocaleFromBranch(branch: any) {
      if (branch?.locales?.length && !branch.locales.includes(i18n.locale)) {
        LocalizationService.setLocale(branch.locales[0])
      }
    }

    static getLocale() {
      return StorageService.getItem(LocalizationService.storageKey) ?? 'en'
    }

    static getCurrentLocale() {
      return i18n.locale
    }

    static updateDirection(locale: string) {
      const shouldRTL = ['ar', 'ur'].includes(locale)
      store.commit('appConfig/SET_RTL', shouldRTL)

      if (document?.documentElement?.setAttribute) {
        document.documentElement.setAttribute('dir', shouldRTL ? 'rtl' : 'ltr')
        document.documentElement.setAttribute('lang', locale)
      }
    }

    static getTranslate(object: any, attributeName = 'name', locale?: string, withoutFallback = false) {
      const currentLocale = locale ?? LocalizationService.getLocale()
      const localesArray = object?.locale ?? object?.locales ?? object
      if (!Array.isArray(localesArray)) {
        return ''
      }
      const attributeValue = localesArray?.find((l: any) => l.locale === currentLocale)?.[attributeName]
      return attributeValue || (withoutFallback ? '' : localesArray?.find((l: any) => !!l?.[attributeName])?.[attributeName] ?? '')
    }

    static getTranslatedLocale(object: any, locale?: string, withoutFallback = false) {
      return LocalizationService.getTranslate(object, 'locale', locale, withoutFallback)
    }

    static getLocaleIndex(object: any, locale: string) {
      const localesArray = object?.locale ?? object?.locales ?? object
      if (!Array.isArray(localesArray)) {
        return -1
      }
      return localesArray?.findIndex((l: any) => l.locale === locale) ?? -1
    }

    static getTranslateArEn(object: any, attributeName = 'name') {
      const currentLocale = LocalizationService.getCurrentLocale()
      const label = object[`${currentLocale}_${attributeName}`] || ''
      return label === '' ? (object[`ar_${attributeName}`] ?? object[`en_${attributeName}`] ?? '') : label
    }

    static getLocalizedText(value: any, locale?: string): string {
      if (value == null || value === '') return ''
      if (typeof value === 'string') return value
      const currentLocale = locale ?? LocalizationService.getCurrentLocale()
      if (typeof value === 'object' && !Array.isArray(value)) {
        if ('ar' in value || 'en' in value) {
          return value[currentLocale] ?? value.en ?? value.ar ?? ''
        }
        return LocalizationService.getTranslate(value) || ''
      }
      return String(value)
    }
}
